// AgentLifecycleSection — the foot of the agent profile, where the two ways to
// stop an agent sit next to each other.
//
// Blueprint agent-lifecycle.md §2.4: disable is the default path and delete is
// the advanced one. Putting them in one section makes the difference legible
// without a paragraph explaining it — disable is a plain button, delete lives
// behind a disclosure and keeps its own confirmation wizard.
//
// Only the owner gets this section in the DOM at all, the same rule RuntimeCard
// holds for start/stop: the server refuses these writes from anyone else, and an
// inert copy of a control offers an action that cannot be taken.
//
// While the agent is disabled the section takes the same amber treatment as
// the page banner, so scrolling down from the banner lands on a second
// confirmation instead of a section that looks like it always does.
import React, { useState } from 'react';
import type { AgentLifecycle } from '../hooks/useAgentLifecycle';

interface Props {
  agentName: string;
  isOwner: boolean;
  lifecycle: AgentLifecycle;
  onDecommission: () => void;
}

export default function AgentLifecycleSection({ agentName, isOwner, lifecycle, onDecommission }: Props) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const { disabled, pending } = lifecycle;

  if (!isOwner) return null;

  return (
    <div
      className={`agent-profile-field agent-lifecycle${disabled ? ' agent-lifecycle-disabled' : ''}`}
      data-testid="agent-lifecycle-section"
    >
      <div className="agent-profile-field-label">LIFECYCLE</div>

      {disabled ? (
        <div className="agent-lifecycle-row">
          <div className="agent-lifecycle-row-text">
            <div className="agent-lifecycle-row-title">Enable agent</div>
            <div className="agent-lifecycle-row-desc">
              Puts {agentName} back to work in the channels it already belongs to. Nothing else changes.
            </div>
          </div>
          <button
            className="btn btn-sm btn-primary"
            data-testid="agent-lifecycle-enable"
            onClick={() => { void lifecycle.enable(); }}
            disabled={pending !== null}
          >
            {pending === 'enable' ? 'Enabling…' : 'Enable'}
          </button>
        </div>
      ) : (
        <div className="agent-lifecycle-row">
          <div className="agent-lifecycle-row-text">
            <div className="agent-lifecycle-row-title">Disable agent</div>
            <div className="agent-lifecycle-row-desc">
              Stops {agentName} receiving messages. It keeps its channels, its history, and its key; one click brings it back.
            </div>
          </div>
          <button
            className="btn btn-sm"
            data-testid="agent-lifecycle-disable"
            onClick={() => { void lifecycle.disable(); }}
            disabled={pending !== null}
          >
            {pending === 'disable' ? 'Disabling…' : 'Disable'}
          </button>
        </div>
      )}

      {lifecycle.disableError && (
        <div className="agent-inline-error" role="alert" data-testid="agent-disable-error">
          <strong>Can't disable {agentName}.</strong> {lifecycle.disableError}
        </div>
      )}

      <div className="agent-lifecycle-row">
        <div className="agent-lifecycle-row-text" style={{ width: '100%' }}>
          <button
            className="agent-advanced-toggle"
            aria-expanded={advancedOpen}
            data-testid="agent-advanced-toggle"
            onClick={() => setAdvancedOpen(open => !open)}
          >
            <span className="chev">▶</span> Advanced actions
          </button>
          {advancedOpen && (
            <div className="agent-lifecycle-advanced">
              <div className="agent-lifecycle-row-text">
                <div className="agent-lifecycle-row-title agent-lifecycle-row-title-danger">Delete agent</div>
                <div className="agent-lifecycle-row-desc">
                  Removes {agentName} from every channel, disconnects it, and deletes the account for good. Its messages stay but lose their author. There is no undo.
                </div>
              </div>
              <button
                className="btn btn-sm btn-danger"
                data-testid="agent-delete-button"
                onClick={onDecommission}
              >
                Delete agent…
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
